import {
  type ReactNode,
  type Ref,
  cloneElement,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  createContext,
} from "react";

import { Button } from "#components/v2/Button";
import { FormFieldAssistiveText } from "#components/v2/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/v2/shared/FormFieldHeader";
import { Tag, type TagProps } from "#components/v2/Tag";
import { TagGroup } from "#components/v2/TagGroup";
import { useUIVersion } from "#hooks";
import { ArrowDownFillIcon, ChevronDownIcon, ClearIcon } from "#icons";
import { cn, customTv } from "#utils";
import { useFilter } from "react-aria";
import {
  ComboBox as RACComboBox,
  ComboBoxStateContext,
  Popover,
  Header,
  Input,
  ListBox,
  ListBoxItem,
  type ListBoxItemRenderProps,
  Text,
  type TextFieldProps as RACTextFieldProps,
  type ComboBoxProps as RACComboBoxProps,
  type Key,
} from "react-aria-components";

const ComboBoxSelectionModeContext = createContext<"single" | "multiple">("multiple");

/** Reserved key for the synthetic "select all" option injected when `selectAllLabel` is set. */
const SELECT_ALL_KEY = "__combobox_select_all__";

interface SelectedKey {
  id: Key;
  name: string;
  group?: string;
}

interface ComboBoxSelectedItems<T extends SelectedKey> {
  items: T[];
  append?: (item: T) => void;
  appendMany?: (items: T[]) => void;
  remove?: (...keys: Key[]) => void;
}

interface FieldProps {
  label?: string;
  placeholder?: string;
  /** Tooltip text shown next to the label in the field header */
  tooltipText?: ReactNode;
  /** Assistive/help or error text shown below the field */
  assistiveText?: ReactNode;
  "aria-label"?: RACTextFieldProps["aria-label"];
  "aria-labelledby"?: RACTextFieldProps["aria-labelledby"];
}

/** ComboBoxProps interface for the ComboBox component */
interface ComboBoxProps<T extends SelectedKey>
  extends
    FieldProps,
    Omit<
      RACComboBoxProps<T>,
      | "children"
      | "validate"
      | "allowsEmptyCollection"
      | "inputValue"
      | "selectedKey"
      | "className"
      | "value"
      | "onSelectionChange"
      | "onInputChange"
      | "selectionMode"
    > {
  /** Full set of selectable items (items with an empty or duplicate `id` are dropped) */
  items: T[];
  /** Controlled selection state and its mutation callbacks */
  selectedItems: ComboBoxSelectedItems<T>;
  /** Additional class name for the field container */
  className?: string;
  /** Ref forwarded to the underlying text input */
  inputRef?: Ref<HTMLInputElement>;
  /** Called with an item's id when it is added to the selection */
  onItemInserted?: (key: Key) => void;
  /** Called with an item's id when it is removed from the selection */
  onItemCleared?: (key: Key) => void;
  /** Called with the current text whenever the search input changes */
  onInputChange?: (value: string) => void;
  /** Renders the dropdown content shown when there are no matching options */
  renderEmptyState?: (inputValue: string) => React.ReactNode;
  /** Renders the tag/chip for a selected item (multiple selection mode) */
  tag: (item: T) => React.ReactNode;
  /** Render function (or static nodes) producing the option for each item */
  children: React.ReactNode | ((item: T) => React.ReactNode);
  /** Whether one or many items can be selected. Defaults to `"multiple"` */
  selectionMode?: "single" | "multiple";
  /** Allow committing a typed value that isn't in the list. Defaults to `false` */
  freeSolo?: boolean;
  /** When set (multiple mode), injects a "select all" option with this label */
  selectAllLabel?: string;
  /** Skip built-in text filtering because `items` are already filtered by the consumer. Defaults to `false` */
  serverSideFilter?: boolean;
  /** Show the clear (X) button that resets the selection in single mode. Defaults to `true` */
  isClearable?: boolean;
}

const comboBoxContainerStyles = customTv({
  base: "group flex w-full min-w-0 flex-col gap-[.15rem]",
});

const comboBoxTriggerStyles = customTv({
  base: cn(
    "relative flex flex-row flex-wrap transition",
    "rounded-1 border border-outline-input-enabled bg-primary",
    "hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover",
    "focus-within:!border-outline-input-focus focus-within:ring-1 focus-within:!ring-outline-input-focus",
  ),
  variants: {
    uiVersion: {
      v1: cn(
        "items-center pl-xs rounded-1 border-legacy-navy-L80 bg-neutral-white pr-px shadow-none !min-h-[2.35rem]",
        "hover:border-legacy-navy-base hover:ring-0",
        "focus-within:border-legacy-orange-base focus-within:ring-1 focus-within:ring-legacy-orange-base",
      ),
      v2: "py-0 pl-lg pr-0 items-center",
    },
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
    isOpen: {
      true: "border-outline-input-focus ring-1 ring-outline-input-focus",
    },
    isDisabled: {
      true: "cursor-not-allowed bg-background-primary border-outline-input-disabled hover:ring-0 hover:border-outline-input-disabled focus-within:!ring-0 focus-within:!border-outline-input-disabled",
    },
    isReadOnly: {
      true: "!cursor-default !bg-background-secondary !border-transparent hover:ring-0 focus-within:!ring-0 focus-within:!border-transparent",
    },
  },
  compoundVariants: [
    {
      uiVersion: "v1",
      isInvalid: true,
      class: "border-outline-negative ring-1 ring-outline-negative",
    },
    {
      uiVersion: "v1",
      isOpen: true,
      class: "border-legacy-orange-base ring-1 ring-legacy-orange-base",
    },
  ],
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxInputStyles = customTv({
  base: "min-w-0 flex-1 shadow-none bg-transparent text-label-medium leading-md-20 focus:outline-none",
  variants: {
    uiVersion: {
      v1: "m-1 px-xxs py-0 text-legacy-navy-base placeholder:text-legacy-navy-L30",
      v2: "px-0 py-xs text-text-primary placeholder:text-text-secondary disabled:text-text-disabled disabled:placeholder:text-text-disabled",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxPopoverStyles = customTv({
  base: "overflow-hidden outline-none rounded-1 border",
  variants: {
    uiVersion: {
      v1: "border-legacy-navy-L80 bg-neutral-white shadow-lg",
      v2: "border-outline-input-enabled bg-background-secondary shadow-md",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxListStyles = customTv({
  base: "max-h-[calc(50vh-80px)] overflow-y-auto",
  variants: {
    uiVersion: {
      v1: "p-0 py-0",
      v2: "px-xxs py-xs flex flex-col gap-y-3xs",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxOptionStyles = customTv({
  base: "w-full text-start outline-none",
  variants: {
    uiVersion: {
      v1: "!rounded-none text-legacy-navy-base font-normal py-1 px-4 hover:bg-legacy-navy-L95 data-[focused]:!text-legacy-navy-base data-[focused]:!bg-legacy-navy-L95",
      v2: cn(
        "rounded-sm px-xs py-3xs cursor-default text-label-small",
        "hover:bg-interactive-secondary-button-hover-background",
        "data-[focused]:bg-interactive-secondary-button-focus-background data-[focused]:outline-none",
        "data-[pressed]:bg-interactive-secondary-button-focus-background",
        "data-[selected]:font-semibold",
      ),
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxChevronButtonStyles = customTv({
  base: "flex shrink-0 items-center justify-center text-label-small transition",
  variants: {
    uiVersion: {
      v1: "size-8 rounded-sm text-legacy-navy-L30 hover:text-legacy-navy-base focus:text-legacy-navy-base",
      v2: "self-stretch pl-xs pr-lg rounded-r-1 text-text-secondary hover:text-text-primary focus:text-text-primary",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxActionsStyles = customTv({
  base: "relative ml-auto flex shrink-0",
  variants: {
    uiVersion: {
      v1: "items-center pr-1",
      v2: "self-stretch items-stretch",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxTagStyles = customTv({
  variants: {
    uiVersion: {
      v1: "rounded-1 border border-legacy-navy-L50 bg-transparent px-3 py-[0.125rem] text-[13px] font-semibold text-legacy-navy-base",
      v2: "",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const comboBoxGroupHeaderStyles = customTv({
  base: "pointer-events-none select-none",
  variants: {
    uiVersion: {
      v1: "px-xs py-sm font-bold text-body-small text-text-primary",
      v2: "px-3xs py-xs text-label-small mx-xs text-text-secondary",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

/** ComboBoxSearchInputProps interface for the ComboBoxSearchInput component */
interface ComboBoxSearchInputProps {
  /** id of the element that labels the input */
  ariaLabelledBy?: string;
  /** Class names applied to the underlying input */
  className: string;
  /** Ref forwarded to the underlying input */
  inputRef?: Ref<HTMLInputElement>;
  /** Whether the field can be interacted with (not disabled or read-only) */
  isInteractive: boolean;
  /** Called when the input loses focus */
  onBlur: () => void;
  /** Keydown handler bound in the capture phase */
  onKeyDownCapture: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Placeholder text shown when the input is empty */
  placeholder?: string;
  /** Reopen the popover on click when it is closed (focus-trigger mode only) */
  reopenOnClick: boolean;
  /** Select the input's text on focus (single-select mode with a value) */
  selectOnFocus: boolean;
}

/**
 * Search input for the ComboBox. Rendered inside RACComboBox so it can read
 * ComboBoxStateContext and reopen the popover on click: with a focus trigger
 * the list only opens on the unfocused → focused transition, so clicking the
 * input while it is already focused (e.g. after a selection) needs to reopen
 * it explicitly. Only applies when `reopenOnClick` is set, so an `input`- or
 * `manual`-triggered ComboBox keeps the consumer's configured behaviour.
 */
const ComboBoxSearchInput = ({
  ariaLabelledBy,
  className,
  inputRef,
  isInteractive,
  onBlur,
  onKeyDownCapture,
  placeholder,
  reopenOnClick,
  selectOnFocus,
}: ComboBoxSearchInputProps) => {
  const state = useContext(ComboBoxStateContext);

  return (
    <Input
      aria-labelledby={ariaLabelledBy}
      className={className}
      onBlur={onBlur}
      onClick={() => {
        if (reopenOnClick && isInteractive && state && !state.isOpen) {
          state.open(null, "manual");
        }
      }}
      onFocus={(e) => {
        if (selectOnFocus) {
          e.target.select();
        }
      }}
      onKeyDownCapture={onKeyDownCapture}
      onPointerDown={isInteractive ? undefined : (e) => e.preventDefault()}
      placeholder={placeholder}
      ref={inputRef}
      tabIndex={isInteractive ? undefined : -1}
    />
  );
};

const ComboBox = <T extends SelectedKey>({
  children,
  items,
  selectedItems,
  onItemCleared,
  onItemInserted,
  onInputChange: externalInputChange,
  className,
  name,
  renderEmptyState,
  assistiveText,
  tooltipText,
  inputRef,
  selectionMode = "multiple",
  label,
  freeSolo = false,
  selectAllLabel,
  serverSideFilter = false,
  isClearable = true,
  ...props
}: ComboBoxProps<T>) => {
  const uiVersion = useUIVersion();
  const tagGroupIdentifier = useId();
  const labelId = useId();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  const { contains } = useFilter({ sensitivity: "base" });
  const selectedKeys = selectedItems.items.map((i) => i.id);

  const filter = useCallback(
    (item: T, filterText: string) => {
      const excludeSelected = selectionMode === "multiple";
      if (excludeSelected && selectedKeys.includes(item.id)) {
        return false;
      }
      // When filtering is server-side, `items` are already filtered by the
      // consumer, so skip the built-in text match.
      return serverSideFilter || contains(item.name, filterText);
    },
    [contains, selectedKeys, selectionMode, serverSideFilter],
  );

  // React Aria keys its collection by `id` and can't render two items that
  // share a key, nor an item with an empty key — either case collapses the
  // entries so a click resolves to the wrong item. Sanitise the input once:
  // drop empty ids and keep only the first occurrence of each id, so the
  // collection only ever sees clean, unique keys.
  const safeItems = useMemo(() => {
    const seen = new Set<Key>();
    return items.filter((item) => {
      if (item.id === "" || item.id == null || seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }, [items]);

  const itemsById = useMemo(() => new Map(safeItems.map((item) => [item.id, item])), [safeItems]);

  const [filterText, setFilterText] = useState("");
  const filteredItems = safeItems.filter((item) => filter(item, filterText));

  const trimmedFilter = filterText.trim();

  const freeSoloItem =
    freeSolo &&
    trimmedFilter !== "" &&
    !itemsById.has(trimmedFilter) &&
    !items.some((item) => item.name === trimmedFilter)
      ? { id: trimmedFilter, name: trimmedFilter }
      : null;

  const selectAllItem =
    selectAllLabel &&
    selectedItems.appendMany &&
    selectionMode === "multiple" &&
    filteredItems.length > 0
      ? { id: SELECT_ALL_KEY, name: selectAllLabel }
      : null;

  const displayItems = [
    ...(selectAllItem ? [selectAllItem as T] : []),
    ...(freeSoloItem ? [freeSoloItem as T] : []),
    ...filteredItems,
  ];

  // Grouping only applies when items carry a `group` and children is a render fn
  const hasGroups = typeof children === "function" && filteredItems.some((item) => item.group);

  const renderGroupedChildren = (renderItem: (item: T) => React.ReactNode) => {
    const renderKeyed = (item: T) => {
      const node = renderItem(item);
      return isValidElement(node) ? cloneElement(node, { key: String(item.id) }) : node;
    };

    const syntheticNodes = [
      ...(selectAllItem ? [renderKeyed(selectAllItem as T)] : []),
      ...(freeSoloItem ? [renderKeyed(freeSoloItem as T)] : []),
    ];

    const groupOrder: string[] = [];
    const grouped = new Map<string, T[]>();

    filteredItems.forEach((item) => {
      const key = item.group ?? "";
      if (!grouped.has(key)) {
        groupOrder.push(key);
        grouped.set(key, []);
      }
      grouped.get(key)?.push(item);
    });

    const groupNodes = groupOrder.flatMap((group) => {
      const groupItems = grouped.get(group) ?? [];

      if (!group) {
        return groupItems.map(renderKeyed);
      }

      // Render the group label as a flat, non-interactive Header
      // React Aria's collection is introspected by React
      // 19.2's profiler render-logger; a flat list of leaf nodes avoids that.
      return [
        <Header className={comboBoxGroupHeaderStyles({ uiVersion })} key={`group-${group}`}>
          {group}
        </Header>,
        ...groupItems.map(renderKeyed),
      ];
    });

    return [...syntheticNodes, ...groupNodes];
  };

  const [typedText, setTypedText] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const singleSelected = selectionMode === "single" ? selectedItems.items[0] : undefined;
  const inputValue =
    selectionMode === "single" ? (typedText ?? singleSelected?.name ?? "") : (typedText ?? "");
  const selectedKey =
    selectionMode === "single" && typedText === null ? (singleSelected?.id ?? null) : null;

  const onRemove = useCallback(
    (keys: Set<Key>) => {
      const key = keys.values().next().value;
      if (key) {
        selectedItems.remove?.(key);
        setTypedText(null);
        onItemCleared?.(key);
      }
    },
    [selectedItems, onItemCleared],
  );

  const onSelectionChange = (key: Key | null) => {
    if (!key) {
      return;
    }

    const clearInput = () => {
      setTypedText(null);
      setFilterText("");
      externalInputChange?.("");
    };

    if (key === SELECT_ALL_KEY) {
      const toAdd = filteredItems.filter((item) => !selectedKeys.includes(item.id));

      selectedItems.appendMany?.(toAdd);
      toAdd.forEach((item) => onItemInserted?.(item.id));
      clearInput();
      return;
    }

    const item =
      itemsById.get(key) ?? (freeSoloItem && key === freeSoloItem.id ? freeSoloItem : undefined);

    if (!item) {
      return;
    }

    if (!selectedKeys.includes(key)) {
      selectedItems.append?.(item as T);
      onItemInserted?.(key);
    }

    clearInput();
  };

  const onInputChange = (value: string) => {
    setTypedText(value);
    setFilterText(value);
    externalInputChange?.(value);
  };

  const popLast = useCallback(() => {
    if (selectedItems.items.length === 0) {
      return;
    }

    const endKey = selectedItems.items[selectedItems.items.length - 1];

    if (endKey) {
      selectedItems.remove?.(endKey.id);
      onItemCleared?.(endKey.id);
    }

    setTypedText(null);
  }, [selectedItems, onItemCleared]);

  const onKeyDownCapture = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && inputValue === "") {
      popLast();
      return;
    }

    // Commit a free-solo value on Enter when no list option is highlighted;
    // otherwise let React Aria select the highlighted option instead.
    if (
      e.key === "Enter" &&
      freeSoloItem &&
      !e.currentTarget.getAttribute("aria-activedescendant")
    ) {
      e.preventDefault();
      e.stopPropagation();
      onSelectionChange(freeSoloItem.id);
    }
  };

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.target.clientWidth);
      }
    });

    observer.observe(trigger);
    return () => {
      observer.unobserve(trigger);
    };
  }, []);

  const tagListClasses = cn(
    "flex flex-wrap gap-xxs outline-hidden",
    selectedItems.items.length > 0 ? "py-1.5" : "",
  );

  const isInteractive = !props.isDisabled && !props.isReadOnly;
  // Default to opening on focus, but let consumers keep RAC's other triggers.
  const menuTrigger = props.menuTrigger ?? "focus";

  return (
    <ComboBoxSelectionModeContext.Provider value={selectionMode}>
      <div className={comboBoxContainerStyles({ className })}>
        <FormFieldHeader
          isDisabled={props.isDisabled}
          isRequired={props.isRequired}
          label={label}
          labelProps={{ id: labelId }}
          tooltipText={tooltipText}
        />
        <div
          className={comboBoxTriggerStyles({
            uiVersion,
            isInvalid: props.isInvalid,
            isOpen,
            isDisabled: props.isDisabled,
            isReadOnly: props.isReadOnly,
          })}
          ref={triggerRef}
        >
          {selectionMode === "multiple" && selectedItems.items.length > 0 ? (
            <TagGroup
              aria-label="Selected items"
              className={cn("py-xs mr-xs", props.isDisabled ? "opacity-50" : "")}
              disabledKeys={isInteractive ? undefined : selectedItems.items.map((i) => i.id)}
              id={tagGroupIdentifier}
              items={selectedItems.items}
              onRemove={isInteractive ? onRemove : undefined}
              tagListClassName={tagListClasses}
            >
              {props.tag}
            </TagGroup>
          ) : null}
          <RACComboBox
            {...props}
            aria-label={label ? undefined : (props["aria-label"] ?? "Available items")}
            aria-labelledby={label ? labelId : props["aria-labelledby"]}
            className="group peer flex min-w-20 flex-1 items-stretch self-stretch"
            // In multi-select mode the input is cleared after every selection
            // (selections live as tags), so propagating `isRequired` to RAC would
            // mark the empty input as natively invalid and block form submission
            // even when items are selected. The required asterisk and `isInvalid`
            // styling are handled separately above.
            inputValue={inputValue}
            isRequired={selectionMode === "multiple" ? false : props.isRequired}
            items={hasGroups ? undefined : displayItems}
            // Open the list as soon as the field is focused. Consumers can
            // override via `menuTrigger`.
            menuTrigger={menuTrigger}
            onInputChange={onInputChange}
            onOpenChange={(open) => {
              setIsOpen(open);
              props.onOpenChange?.(open);
            }}
            onSelectionChange={onSelectionChange}
            selectedKey={selectedKey}
            allowsEmptyCollection
            shouldFocusWrap
          >
            <div className="inline-flex min-w-0 flex-1 flex-nowrap items-stretch px-0">
              <ComboBoxSearchInput
                ariaLabelledBy={label ? labelId : undefined}
                className={comboBoxInputStyles({ uiVersion })}
                inputRef={inputRef}
                isInteractive={isInteractive}
                onBlur={() => {
                  setTypedText(null);
                  setFilterText("");
                  externalInputChange?.("");
                }}
                onKeyDownCapture={onKeyDownCapture}
                placeholder={props.placeholder}
                reopenOnClick={menuTrigger === "focus"}
                selectOnFocus={selectionMode === "single" && selectedItems.items.length > 0}
              />

              <div className={comboBoxActionsStyles({ uiVersion })}>
                {selectionMode === "single" && isClearable && selectedItems.items.length > 0 ? (
                  <Button
                    IconLeft={<ClearIcon size={16} />}
                    aria-label="Clear selection"
                    className={cn(
                      "p-0 flex items-center justify-center transition-colors",
                      uiVersion === "v1"
                        ? "text-legacy-navy-L30 hover:text-legacy-navy-base"
                        : "text-text-secondary hover:text-text-primary",
                    )}
                    isDisabled={props.isDisabled || props.isReadOnly}
                    onPress={() => {
                      if (selectedItems.items[0]) {
                        const cleared = selectedItems.items[0];
                        selectedItems.remove?.(cleared.id);
                        onItemCleared?.(cleared.id);
                        setTypedText(null);
                        setFilterText("");
                        externalInputChange?.("");
                      }
                    }}
                    slot={null}
                    variant="plain"
                  />
                ) : null}
                <Button
                  aria-label={isOpen ? "Close dropdown" : "Open dropdown"}
                  className={comboBoxChevronButtonStyles({
                    uiVersion,
                  })}
                  isDisabled={props.isDisabled || props.isReadOnly}
                  variant="plain"
                >
                  {uiVersion === "v1" ? (
                    <span className="p-1">
                      <ArrowDownFillIcon
                        className={cn("size-icon-l transition", isOpen ? "rotate-180" : "")}
                      />
                    </span>
                  ) : (
                    <ChevronDownIcon
                      className={cn("size-lg transition", isOpen ? "rotate-180" : "")}
                    />
                  )}
                </Button>
              </div>
            </div>
            <Popover
              className={comboBoxPopoverStyles({ uiVersion })}
              style={{ minWidth: `${width}px` }}
              trigger="ComboBox"
              triggerRef={triggerRef}
              isNonModal
            >
              <ListBox
                className={comboBoxListStyles({ uiVersion })}
                renderEmptyState={() =>
                  renderEmptyState ? (
                    renderEmptyState(inputValue)
                  ) : (
                    <Text
                      className={cn(
                        "block p-3 text-label-small",
                        uiVersion === "v1" ? "text-legacy-navy-L30" : "text-text-secondary",
                      )}
                      slot="description"
                    >
                      {inputValue ? (
                        <>
                          No results found for:{" "}
                          <strong
                            className={cn(
                              "font-medium",
                              uiVersion === "v1" ? "text-legacy-navy-base" : "text-fg",
                            )}
                          >
                            {inputValue}
                          </strong>
                        </>
                      ) : (
                        "No options"
                      )}
                    </Text>
                  )
                }
                selectionMode={selectionMode}
              >
                {hasGroups && typeof children === "function"
                  ? renderGroupedChildren(children)
                  : children}
              </ListBox>
            </Popover>
          </RACComboBox>
        </div>
        <FormFieldAssistiveText isDisabled={props.isDisabled} isInvalid={props.isInvalid}>
          {assistiveText}
        </FormFieldAssistiveText>
        {name ? <input name={name} value={selectedKeys.join(",")} hidden readOnly /> : null}
      </div>
    </ComboBoxSelectionModeContext.Provider>
  );
};

const ComboBoxTag = (props: TagProps) => {
  const uiVersion = useUIVersion();
  const selectionMode = useContext(ComboBoxSelectionModeContext);

  return (
    <Tag
      {...props}
      className={cn(
        comboBoxTagStyles({ uiVersion }),
        selectionMode !== "multiple" ? "bg-transparent border-none" : "",
        props.className,
        "mr-0",
      )}
      color={props.color ?? (uiVersion === "v1" ? "grey" : undefined)}
    />
  );
};
type ComboBoxOptionProps = React.ComponentProps<typeof ListBoxItem>;

const ComboBoxOption = ({ className, ...props }: ComboBoxOptionProps) => {
  const uiVersion = useUIVersion();

  return (
    <ListBoxItem
      {...props}
      className={(renderProps) =>
        cn(
          comboBoxOptionStyles({ uiVersion }),
          uiVersion === "v1" && renderProps.isSelected ? "bg-legacy-navy-L95" : "",
          typeof className === "function"
            ? className(
                renderProps as ListBoxItemRenderProps & {
                  defaultClassName: string | undefined;
                },
              )
            : className,
        )
      }
    />
  );
};

ComboBox.Tag = ComboBoxTag;
ComboBox.Option = ComboBoxOption;

export type { ComboBoxProps, SelectedKey };
export { ComboBox };
